import React, { FormEvent, FC } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import {
  useApisManagersSkillEvaluationStandardSummaryCategoriesCreate,
  useBoolean,
  useInput,
  useMultiDivisionMultiSectionDropdown,
  useProvidersCurrentEmployee,
} from "~/hooks";

import { Form, FormSubmitButton } from "~/components/atoms";
import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";
import { LabelWithTextField } from "~/components/molecules";
import { AllDistributionCheckboxMultiParentsWithMultiChildrenDropdownField } from "~/components/organisms";

import { PublicStatus } from "~/domains";

export const ManagersSkillEvaluationStandardSummaryCategoriesNew: FC = () => {
  const breadCrumbItems = [
    {
      label: "スキルマップ",
      href: "/managers/skill_evaluation_standard_summary_categories",
    },
    {
      label: "新規作成",
      href: "/managers/skill_evaluation_standard_summary_categories/new",
    },
  ];

  const navigate = useNavigate();
  const { currentEmployee } = useProvidersCurrentEmployee();

  const { mutate: postDoRequest, isLoading: isSubmitting } =
    useApisManagersSkillEvaluationStandardSummaryCategoriesCreate();

  const {
    isChecked: allBusinessSectionsDistribution,
    handleChange: onChangeAllBusinessSectionsDistribution,
  } = useBoolean(false);

  const [name] = useInput("");
  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions,
    selectableSections: currentEmployee?.businessSections,
  });

  const handleDraftSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const body = {
      businessSectionIds: optionSelectedSections.map((section) => section.id),
      name: name.value,
      publicStatusId: PublicStatus.DRAFTED.id,
      allBusinessSectionsDistribution,
    };
    postDoRequest(
      {
        body,
      },
      {
        onSuccess: (data) => {
          toast(data.message);
          navigate(
            `/managers/skill_evaluation_standard_summary_categories/${data.skillEvaluationStandardSummaryCategory.id}/edit`,
          );
        },
      },
    );
  };

  return (
    <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent withRightSidebar>
        <Form className="space-y-6" onSubmit={handleDraftSubmit}>
          <AllDistributionCheckboxMultiParentsWithMultiChildrenDropdownField
            allParents={selectableDivisions}
            parentsValue={selectedDivisions}
            parentsOnChange={onDivisionChange}
            parentRequired={true}
            parentLabel="配布先: 部署"
            allChildren={optionSelectableSections}
            childrenValue={optionSelectedSections}
            childrenOnChange={onSectionChange}
            childRequired={true}
            childLabel="配布先: 課"
            allBusinessSectionsDistribution={allBusinessSectionsDistribution}
            onChangeAllBusinessSectionsDistribution={
              onChangeAllBusinessSectionsDistribution
            }
            currentEmployeeRole={currentEmployee?.employeeRole}
          />
          <LabelWithTextField
            labelText="名前"
            type="text"
            name="name"
            placeholder="名前"
            required
            value={name.value}
            onChange={name.onChange}
          />
          <FormSubmitButton
            value="スキルマップ項目の作成"
            color="primary"
            className="w-full"
            isReadOnly={isSubmitting}
          />
        </Form>
      </EmployeeLayoutMainContent>
      <EmployeeLayoutRightSidebar />
    </EmployeeLayout>
  );
};
